var Vec3f = require('./vec3f');

/**
 * Construct a new Rotation object.<br><br>
 * 
 * #### new Rotation(x, y, z, angle)
 * #### new Rotation([x, y, z, angle])
 * 
 * where 
 * - _x_, _y_, and _z_ are the axis of the rotation; 
 * - _angle_ is the angle of the rotation (in radians).<br><br>
 * 
 * #### new Rotation(<{@link Vec3f}> axis, angle)
 * 
 * where 
 * - _axis_ is the axis of rotation;
 * - _angle_ is the angle of the rotation (in radians).<br><br>
 * 
 * #### new Rotation(<{@link Vec3f}> fromVector, <{@link Vec3f}> toVector)
 * 
 * where
 * - _fromVector_ and _toVector_ are normalized and the rotation value that would rotate from the _fromVector_ to the _toVector_ is stored in the object.
 * 
 * @class The Rotation object corresponds to a VRML SFRotation field. It has four numeric properties: x, y, z (the axis of rotation) and angle.
 */
function Rotation( /*oVec3f, angle | oVec3f, oVec3f | x, y, z, angle*/ ) {
    if (arguments.length == 4) {
        /**
         * First value of the axis vector.
         * @type number
         * @default 0
         */
        this.x = arguments[0];
        /**
         * Second value of the axis vector.
         * @type number
         * @default 1
         */
        this.y = arguments[1];
        /**
         * Third value of the axis vector.
         * @type number
         * @default 0
         */
        this.z = arguments[2];
        /**
         * The angle of the rotation (in radians).
         * @type number
         * @default 0
         */
        this.angle = arguments[3];
    } else if (arguments.length == 2) {
        if (typeof (arguments[1]) == 'number') {
            this.setAxis(arguments[0]);
            this.angle = arguments[1];
        } else {
            var FLT_EPSILON = 1.192092896e-07;
            var v = arguments[0].normalize();
            var v1 = arguments[1].normalize();
            var axis = v.cross(v1);
            var len = axis.length();
            if (len <= FLT_EPSILON) {
                if (v.dot(v1) < 0.0) {
                    axis = v.cross(new Vec3f(1, 0, 0));
                    this.angle = Math.PI;
                    len = axis.length();
                    if (len <= FLT_EPSILON)
                        axis = v.cross(new Vec3f(0, 1, 0));
                    axis = axis.normalize();

                } else {
                    axis = new Vec3f(0, 1, 0);
                    this.angle = 0.0;
                }
            } else {
                this.angle = Math.atan2(len, v.dot(v1));
                axis = axis.normalize();
            }
            this.setAxis(axis);
        }
    } else if (arguments.length == 1 && Array.isArray(arguments[0]) && arguments[0].length === 4) {
        this.x = arguments[0][0];
        this.y = arguments[0][1];
        this.z = arguments[0][2];
        this.angle = arguments[0][3];
    } else {
        this.x = 0;
        this.y = 1;
        this.z = 0;
        this.angle = 0;
    }
}

/**
 * Returns array represents rotations fields [x, y, z, angle].
 * @returns {number[]} Array of four floats.
 */
Rotation.prototype.toArray =
    function () {
        return [this.x, this.y, this.z, this.angle];
    };

/**
 * Returns the value of oVec3f multiplied by the matrix corresponding to this object's rotation.
 * @param {Vec3f} oVec3f The Vec3f object.
 * @returns {Vec3f}
 */
Rotation.prototype.multVec =
    function (oVec3f) {
        var e = -this.angle;
        var s = Math.sin(e);
        var c = Math.cos(e);

        var f = this.x;
        var g = this.y;
        var h = this.z;

        var x = oVec3f.x;
        var y = oVec3f.y;
        var z = oVec3f.z;

        var p = (f * x + g * y + h * z) * (1 - c);

        var px = f * p + c * x + s * (h * y - g * z);
        var py = g * p + c * y + s * (f * z - h * x);
        var pz = h * p + c * z + s * (g * x - f * y);
        return new Vec3f(px, py, pz);
    };

/**
 * Returns the inverse of this object's rotation.
 * @returns {Rotation}
 */
Rotation.prototype.inverse =
    function () {
        return new Rotation(this.x, this.y, this.z, -this.angle);
    };

/**
 * Returns the object multiplied by the passed value.
 * @param {Rotation} oRotation The Rotation object.
 * @returns {Rotation}
 */
Rotation.prototype.multiply =
    function (oRotation) {
        var angle = oRotation.angle;
        var Sa = Math.sin(angle / 2);
        var Ca = Math.cos(angle / 2);
        var As = oRotation.getAxis().multiply(Sa);

        var Sb = Math.sin(this.angle / 2);
        var Cb = Math.cos(this.angle / 2);
        var Bs = this.getAxis().multiply(Sb);

        var Cab = Ca * Cb - As.dot(Bs);

        var ABs = As.multiply(Cb).add(Bs.multiply(Ca)).add(Bs.cross(As));

        var msq = ABs.x * ABs.x + ABs.y * ABs.y + ABs.z * ABs.z;

        var result = new Rotation(0, 0, 1, 0);
        if (msq !== 0) {
            var Sab = Math.sqrt(msq);
            result.setAxis(ABs.divide(Sab));
            result.angle = 2 * Math.atan2(Sab, Cab);
        }
        return result;
    };

/**
 * Sets the axis of rotation to the value passed in oVec3f.
 * @param {Vec3f} oVec3f The Vec3f object.
 */
Rotation.prototype.setAxis =
    function (oVec3f) {
        this.x = oVec3f.x;
        this.y = oVec3f.y;
        this.z = oVec3f.z;
    };

/**
 * Returns the axis of rotation.
 * @returns {Vec3f}
 */
Rotation.prototype.getAxis =
    function () {
        return new Vec3f(this.x, this.y, this.z);
    };

/**
 * Returns a string containing the value of x, y, z, and angle separated by spaces.
 * @returns {string}
 */
Rotation.prototype.toString =
    function () {
        return this.toArray().join(' ');
    };

module.exports = Rotation;